<script setup lang="ts">
import type { ImageCategory } from "@/rvapi";
import type { Frame } from "@/types/frames";
import { useRouter } from "vue-router";
import { ref, onMounted, computed } from "vue";
import { edit, upload, trash_can } from "pn-design-assets/pn-assets/icons";
import ConfirmationModal from "../modals/ConfirmationModal.vue";
import { useFrames } from "@/stores/frames";
import { useImageStore } from "@/stores/images";

const router = useRouter();
const categories = ref<Record<number, ImageCategory>>([]);
const dragsource = ref("");
const dragtarget = ref("");
const loaded = ref(false);
const framesStore = useFrames();
const imageStore = useImageStore();
import { storeToRefs } from "pinia";
const { allFrames, notification } = storeToRefs(framesStore);
const setCategories = (c: ImageCategory[]) => {
  for (const ic of c) {
    categories.value[ic.categoryId] = ic;
  }
};
const showDeleteConfirmationModal = ref(false);
let selectedFrametoDelete: number[] = [];
const showSpinner = ref(false);
const deleteFramesDesc = ref("");

onMounted(async () => {
  const catpromise = imageStore.getImageCategories();
  await getSortedFrames();
  setCategories(await catpromise);
  loaded.value = true;
});

const getSortedFrames = async () => {
  await framesStore.getAllFrames();
  framesStore.normalizeGroupIds();
  if (allFrames.value.length > 0) {
    allFrames.value.sort((frame1, frame2) => frame1.frameId - frame2.frameId);
  }
};

const groupedFrames = computed(() => {
  return framesStore.framesGroupedByGroupId;
});

const dragenter = (e: any) => {
  e.preventDefault();
};
const dragover = (e: any) => {
  dragtarget.value = e.target.closest("tr").dataset.id;

  e.target.closest("tr").classList.add("target");
  e.preventDefault();
};
const dragleave = (e: any) => {
  e.target.closest("tr").classList.remove("target");
};
const dragstart = (e: any) => {
  dragsource.value = e.target.closest("tr").dataset.id;
};
const drop = async (e: any) => {
  e.target.closest("tr").classList.remove("target");
  const sourceIx = allFrames.value.findIndex(
    (c) => c.frameId.toString() === dragsource.value,
  );
  const source = allFrames.value.splice(sourceIx, 1)[0];
  if (dragtarget.value === "last") {
    allFrames.value.push(source);
  } else {
    const targetIx = allFrames.value.findIndex(
      (c) => c.frameId.toString() === dragtarget.value,
    );
    allFrames.value.splice(targetIx, 0, source);
  }

  dragsource.value = "";
  dragtarget.value = "";
  await framesStore.reorderFrames(allFrames.value.map((c) => c.frameId));
};
const frameCategory = (f: Frame) => {
  if (!f || !f.categories || f.categories.length === 0) {
    return "-";
  }
  const cat = categories.value[f.categories[0]];
  return cat?.swedish ?? cat?.english ?? cat?.danish ?? cat?.norwegian ?? "-";
};

const handleDeleteConfirmationShow = (frameId: number) => {
  reset();
  showDeleteConfirmationModal.value = true;
  selectedFrametoDelete.push(frameId);
  deleteFramesDesc.value = `Are you sure you want to delete the frame with id ${frameId}?`;
};

const handleGroupDeleteConfirmationShow = (frames: Frame[]) => {
  reset();
  showDeleteConfirmationModal.value = true;
  const frameIds = frames.map((frame) => {
    selectedFrametoDelete.push(frame.frameId);
    return frame.frameId;
  });
  deleteFramesDesc.value = `Are you sure you want to delete frames with ids ${frameIds[0]}, ${frameIds[1]}, ${frameIds[2]}?`;
};
const handleUploadFrame = () => {
  reset();
  router.push("/uploadframes");
};

const handleEditFrame = (frameId: number) => {
  reset();
  router.push(`/frames/${frameId}`);
};

const handleEditFrameGroup = (groupId: string | undefined) => {
  reset();
  router.push(`/framesByGroup/${groupId}`);
};

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
};

const deleteFrame = async () => {
  try {
    showSpinner.value = true;
    for (const frameId of selectedFrametoDelete) {
      await framesStore.deleteFrame(frameId);
    }
    await getSortedFrames();
    framesStore.setNotificationAlert({
      show: true,
      message: "Frames deleted successfully",
      type: "success",
      autoHide: true,
    });
  } catch {
    framesStore.setNotificationAlert({
      show: true,
      type: "error",
      message: "There was an error in deleting one or more frames",
      autoHide: true,
    });
    console.log("Error in deleting frame");
  } finally {
    showDeleteConfirmationModal.value = false;
    showSpinner.value = false;
    window.scrollTo(0, 0);
  }
};

const reset = (): void => {
  framesStore.setNotificationAlert({
    show: false,
    message: "",
    type: "error",
    autoHide: false,
  });
  selectedFrametoDelete = [];
};
</script>

<template>
  <h1>Frames</h1>
  <pn-toast
    v-if="notification.show"
    :appearance="notification.type"
    :text="notification.message"
    closable
  ></pn-toast>
  <p>
    <pn-button
      label="Upload Frames"
      appearance="light"
      variant="outlined"
      :icon="upload"
      @pnClick="handleUploadFrame"
    ></pn-button>
  </p>
  <div :class="[{ spinner: !loaded }]">
    <pn-spinner size="3" v-show="!loaded" />
  </div>
  <div v-show="loaded">
    <pn-table bordered="true">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Category</th>
            <th class="text-center">id</th>
            <th colspan="3" class="action">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(frames, _groupId) in groupedFrames" :key="_groupId">
            <tr
              v-if="frames.length > 1"
              :class="frames[0].hidden ? 'hidden' : ''"
              class="normal-row"
            >
              <td>
                <div>
                  <img
                    v-for="frame in frames"
                    class="thumb"
                    :key="frame.frameId"
                    :src="frame.thumbUrl"
                  />
                </div>
              </td>
              <td>{{ frameCategory(frames[0]) }}</td>
              <td>
                <div v-for="frame in frames" :key="frame.frameId">
                  <p>{{ frame.frameId }}</p>
                </div>
              </td>
              <td class="action-buttons">
                <pn-button
                  small
                  variant="outlined"
                  appearance="light"
                  :icon="edit"
                  tooltip="Edit"
                  @click="handleEditFrameGroup(frames[0].groupId)"
                />
              </td>
              <td class="action-buttons">
                <pn-button
                  small
                  variant="outlined"
                  appearance="light"
                  :icon="trash_can"
                  tooltip="Delete"
                  @click="handleGroupDeleteConfirmationShow(frames)"
                />
              </td>
              <td></td>
            </tr>
            <tr
              v-else
              v-for="frame in frames"
              :key="frame.frameId"
              :data-id="frame.frameId"
              draggable="true"
              @dragstart="dragstart"
              @drop="drop"
              @dragenter="dragenter"
              @dragleave="dragleave"
              @dragover="dragover"
              :class="frame.hidden ? 'hidden' : ''"
              class="normal-row"
            >
              <td>
                <img class="thumb" :src="frame.thumbUrl" />
              </td>
              <td>{{ frameCategory(frame) }}</td>
              <td>{{ frame.frameId }}</td>
              <td class="action-buttons">
                <pn-button
                  small
                  variant="outlined"
                  appearance="light"
                  :icon="edit"
                  tooltip="Edit"
                  @click="handleEditFrame(frame.frameId)"
                />
              </td>
              <td class="action-buttons">
                <pn-button
                  small
                  variant="outlined"
                  appearance="light"
                  :icon="trash_can"
                  tooltip="Delete"
                  @click="handleDeleteConfirmationShow(frame.frameId)"
                />
              </td>
              <td></td>
            </tr>
          </template>
        </tbody>
      </table>
    </pn-table>
    <ConfirmationModal
      :showLoader="showSpinner"
      :open="showDeleteConfirmationModal"
      :heading="deleteFramesDesc"
      @onCancel="closeDeleteConfirmationModal"
      @onConfirm="deleteFrame"
    />
  </div>
</template>

<style lang="scss" scoped>
.action {
  text-align: center;
}

.action-buttons {
  padding-left: 1.5rem;
  padding-right: 0.5rem;
}
.thumb {
  width: 100px;
  margin: 4px;
}
.target {
  border-top: 3px dashed gray;
}
.hidden {
  filter: saturate(0);
  text-decoration: line-through;
}

td > pn-icon {
  padding-bottom: 0.3rem;
}

.flags > span {
  margin-left: 5px;
}
.flags img {
  border: 0;
  border-radius: 0;
  width: 20px;
}

img {
  border: 1px dashed gray;
  border-radius: 1rem;
  width: 300px;
}

.normal-row {
  background: none;
  border-bottom: 0;
}
</style>
